import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbInlineErrorNotification } from "abreact/feedback/AbInlineErrorNotification";
import { AbDecoratorBar } from "abreact/form/AbDecoratorBar";
import { AbFieldDecoratorFactory } from "abreact/form/AbFieldDecoratorFactory";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbErrorBoundary } from "abreact/incubator/ui/component/AbErrorBoundary";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbMenuButton } from "abreact/menu/AbMenuButton";
import { AbLabel } from "abreact/misc/AbLabel";
import * as React from "react";
export function AbField(props) {
  const controlRef = React.useRef(null);
  const inputId = useFieldInputID(props.inputId, props.label, controlRef);
  const childElement = props.children;
  const fullWidth = props.fullWidth || props.renderError !== undefined;
  const stackLabel = props.stackLabel ?? (props.autoLabelPlacement ? !props.singleLine : false);
  const standalone = props.standalone ?? (props.autoLabelPlacement && stackLabel ? true : false);
  const extraClasses = [props.modified && "ab-is-modified", props.required && "ab-is-required", props.readOnly && "ab-is-read-only", props.singleLine && "ab-single-line", props.menuItems && props.menuItems.length > 0 && "ab-has-action-menu", fullWidth && "ab-field-full-width", standalone && "ab-standalone-field", stackLabel && "ab-stack-label-field"];
  const label = /*#__PURE__*/React.createElement(AbLabel, {
    className: ReactUtils.classNamesString([props.className, "ab-field-label", ...extraClasses]),
    style: props.style,
    dataTestId: props.labelDataTestId,
    label: props.label,
    for: inputId,
    description: props.description,
    tooltip: props.tooltip
  });
  const decorators = AbFieldDecoratorFactory.createFieldDecorators({
    decorators: props.renderError !== undefined ? [] : props.decorators,
    required: props.required
  });
  const controlProps = ReactUtils.componentProps("ab-field", props, [...extraClasses, "ab-field-decorated-control"]);
  const controlElement = typeof childElement !== "string" ? /*#__PURE__*/React.cloneElement(childElement, {
    className: ReactUtils.classNamesString(["ab-field-control", childElement.props.className])
  }) : props.children;
  const renderedDecoratedControl = /*#__PURE__*/React.createElement("div", _extends({}, props.standalone ? {
    className: ReactUtils.classNamesString(["ab-field-decorated-control", ...extraClasses])
  } : controlProps, {
    ref: controlRef
  }), props.renderError ? /*#__PURE__*/React.createElement(AbInlineErrorNotification, {
    message: props.renderErrorMessage ?? AbReactResources.error_unexpectedInternalError(),
    error: props.renderError
  }) : controlElement, /*#__PURE__*/React.createElement(AbDecoratorBar, {
    className: "ab-field-decorators",
    decorators: decorators
  }), props.menuItems && /*#__PURE__*/React.createElement(AbMenuButton, {
    className: "ab-field-action-menu ab-no-border-button",
    icon: "absdk:more",
    menuItems: props.menuItems
  }));

  /* For non-stack Label form groups, we don't return the outer div of an "ab-field", but rather let the CSS grid do the layout */
  const fieldFragment = /*#__PURE__*/React.createElement(React.Fragment, null, label, /*#__PURE__*/React.createElement(AbErrorBoundary, null, renderedDecoratedControl));
  if (!stackLabel && !standalone) {
    return fieldFragment;
  } else {
    return /*#__PURE__*/React.createElement("div", ReactUtils.componentProps("ab-field", props, extraClasses), fieldFragment);
  }
}
function useFieldInputID(inputId, label, ref) {
  const [fieldInputId, setFieldInputId] = React.useState(undefined);
  React.useLayoutEffect(() => {
    if (ref.current && inputId === undefined) {
      const matchingInputId = getFieldInputID(ref.current);
      if (matchingInputId && matchingInputId !== fieldInputId) {
        setFieldInputId(matchingInputId);
      }
    } else if (fieldInputId) {
      setFieldInputId(undefined);
    }
  }, [ref, inputId, fieldInputId]);
  return inputId ?? fieldInputId;
}
function getFieldInputID(controlElement) {
  // TODO AndyA: look for a data attribute first on the control element
  const firstInput = controlElement.querySelector("input, textarea");
  if (firstInput) {
    return firstInput?.getAttribute("id") ?? undefined;
  }
  const firstButton = controlElement.querySelector("button");
  return firstButton?.getAttribute("id") ?? undefined;
}
//# sourceMappingURL=AbField.js.map
